// src/pages/DashboardPage.js
import React, { useState, useEffect } from 'react';
import { LuWallet, LuChartBar, LuCircleCheck, LuClock, LuArrowUp, LuArrowDown, LuCircleX } from 'react-icons/lu';
import apiClient from '../api/apiClient';
// --- Import Your Logo Images ---
import zerodhaLogo from '../assets/brokers/zerodha.png';
import dhanLogo from '../assets/brokers/dhan.jpeg';
import { useSelector } from 'react-redux';

// --- The Main Component ---
const DashboardPage = () => {
  const { user } = useSelector((state) => state.auth);

  const [isBridgeOnline, setIsBridgeOnline] = useState(true);
  const [dashboardStats, setDashboardStats] = useState([]);
  const [connectedBrokers, setConnectedBrokers] = useState([]);
  const [recentSignals, setRecentSignals] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleKillSwitch = () => {
    if (window.confirm("Are you sure you want to exit all active positions? This action cannot be undone.")) {
      alert("Exiting all positions now!");
      console.log("KILL SWITCH ACTIVATED");
    }
  };

  const getGreeting = () => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) return "Good morning";
    if (currentHour < 17) return "Good afternoon";
    return "Good evening";
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const statsResponse = await apiClient.get('/dashboard/stats');
        setDashboardStats(statsResponse.data);

        const brokersResponse = await apiClient.get('/dashboard/brokers');
        // Add logos based on broker name
        const brokersWithLogos = brokersResponse.data.map(broker => {
          let logo = null;
          if (broker.name.toLowerCase().includes('zerodha')) logo = zerodhaLogo;
          else if (broker.name.toLowerCase().includes('dhan')) logo = dhanLogo;
          return { ...broker, logo };
        });
        setConnectedBrokers(brokersWithLogos);

        const signalsResponse = await apiClient.get('/dashboard/recent-signals');
        setRecentSignals(signalsResponse.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchDashboardData();
  }, []);

  if (!user) {
    return <div className="p-4 text-center text-red-600">User not authenticated. Please login again.</div>;
  }

  return (
    <div className="bg-slate-50 min-h-screen p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">

      {/* === Unified & Responsive Header === */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center space-y-3 sm:space-y-0">
        <div>
          <p className="text-sm sm:text-md text-slate-500">{getGreeting()}</p>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800">Hi, {user.name}</h1>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-4">
          <div className="flex items-center space-x-1 sm:space-x-2 p-2 bg-white rounded-lg border border-slate-200 text-xs sm:text-sm">
            <span className={`font-semibold ${isBridgeOnline ? 'text-green-600' : 'text-red-600'}`}>
              {isBridgeOnline ? 'Online' : 'Offline'}
            </span>
            <input
              type="checkbox"
              checked={isBridgeOnline}
              onChange={() => setIsBridgeOnline(!isBridgeOnline)}
              className="toggle toggle-xs sm:toggle-sm toggle-success"
            />
          </div>
          <button
            onClick={handleKillSwitch}
            className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-red-600 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
          >
            <LuCircleX size={14} className="sm:size-[18px]" />
            <span className="hidden xs:inline">Kill Switch</span>
            <span className="xs:hidden">Kill</span>
          </button>
        </div>
      </div>

      {/* === Responsive Main Content Grid === */}
      {/* Mobile: 1 column | Tablet: 2 columns | Desktop: 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">

        {/* --- Stats Grid --- */}
        {/* Mobile: 2 columns | Tablet: 4 columns | Desktop: 4 columns */}
        <div className="col-span-1 md:col-span-2 lg:col-span-3">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {dashboardStats.map(stat => (
              <div key={stat.id} className="bg-white p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 flex items-center space-x-2 sm:space-x-3 hover:shadow-md transition-shadow">
                <div className="p-2 sm:p-3 bg-slate-100 rounded-full">
                  {React.cloneElement(
                    stat.label === "Today's P&L" ? <LuWallet /> :
                      stat.label === 'Active Positions' ? <LuClock /> :
                        stat.label === 'Total Trades' ? <LuChartBar /> :
                          <LuCircleCheck />,
                    { size: window.innerWidth < 640 ? 18 : 24 }
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-slate-500 truncate">{stat.label}</p>
                  <p className={`text-lg sm:text-xl md:text-2xl font-bold truncate ${stat.isProfit ? 'text-green-600' : 'text-slate-800'}`}>
                    {stat.value}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* --- Brokers Card --- */}
        {/* Mobile: full width | Tablet: full width (first column) | Desktop: 1 column */}
        <div className="bg-white p-4 sm:p-5 rounded-xl sm:rounded-2xl shadow-sm border border-slate-200">
          <h2 className="text-base sm:text-lg font-bold text-slate-800 mb-3 sm:mb-4">Connected Brokers</h2>
          <div className="space-y-3 sm:space-y-4">
            {connectedBrokers.map(broker => (
              <div key={broker.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                  <img src={broker.logo} alt={broker.name} className="h-8 w-8 sm:h-10 sm:w-10 rounded-full flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-700 text-sm sm:text-base truncate">{broker.name}</p>
                    <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Active</span>
                  </div>
                </div>
                <p className="font-semibold text-slate-800 text-sm sm:text-base ml-2 truncate">{broker.balance}</p>
              </div>
            ))}
          </div>
        </div>

        {/* --- Signals Card --- */}
        {/* Mobile: full width | Tablet: full width (second column) | Desktop: 2 columns */}
        <div className="bg-white p-4 sm:p-5 rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 md:col-span-1 lg:col-span-2">
          <h2 className="text-base sm:text-lg font-bold text-slate-800 mb-3 sm:mb-4">Recent Signals</h2>
          <div className="space-y-3 sm:space-y-4">
            {recentSignals.map(signal => (
              <div key={signal.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                  <div className={`p-1.5 sm:p-2 rounded-full flex-shrink-0 ${signal.signal === 'BUY' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                    {signal.signal === 'BUY' ? <LuArrowUp size={14} className="sm:size-[16px]" /> : <LuArrowDown size={14} className="sm:size-[16px]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-700 text-sm sm:text-base truncate">{signal.name}</p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                        Entry: ₹{signal.entry_price}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-50 text-red-600">
                        SL: ₹{signal.sl_price}
                      </span>
                      {signal.exit_price && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
                          Exit: ₹{signal.exit_price}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-slate-500 truncate">
                        {signal.status} • Qty: {signal.quantity}
                      </p>
                      <span className="text-xs text-slate-400 truncate ml-2">
                        {new Date(signal.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                </div>
                <p className={`font-semibold text-sm sm:text-base ml-2 truncate ${parseFloat(signal.pnl.replace(/[^\d.-]/g, '')) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {signal.pnl}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default DashboardPage;

